// src/pages/settings/notification-management-page/components/RecipientSelectorModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Select, Input, Spin, message, Space } from 'antd';
import { ApiRequest } from '../../../../services/api/api';
import { urlKey } from '../../../../services/api/api-urls';

const { Option } = Select;

interface EmployeeOption {
  value: number;
  label: string;
  departmentId: number | null;
  departmentName?: string;
}

interface DepartmentOption {
  value: number;
  label: string;
}

interface RecipientSelectorModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (employeeIds: number[]) => void;
  initialEmployeeIds: number[] | null;
}

const RecipientSelectorModal: React.FC<RecipientSelectorModalProps> = ({
  visible,
  onClose,
  onConfirm,
  initialEmployeeIds
}) => {
  const [isAllRecipients, setIsAllRecipients] = useState(true);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [allEmployees, setAllEmployees] = useState<EmployeeOption[]>([]);
  const [allDepartments, setAllDepartments] = useState<DepartmentOption[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<EmployeeOption[]>([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize state based on initialEmployeeIds
  useEffect(() => {
    if (visible) {
      const isAll = !initialEmployeeIds || initialEmployeeIds.length === 0;
      setIsAllRecipients(isAll);
      setSelectedEmployeeIds(isAll ? [] : [...initialEmployeeIds]);
      setSelectedDepartmentIds([]);
      setSearchText('');
      setHasUnsavedChanges(false);
    }
  }, [visible, initialEmployeeIds]);

  // Load employees and departments on mount
  useEffect(() => {
    if (visible) {
      loadData();
    }
  }, [visible]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load employees
      const empResult = await ApiRequest({
        otherUrl: 'Employee/GetAllEmployee',
        type: 'get',
      });

      const employees = empResult?.data?.data ?? empResult?.data ?? [];
      const employeeOptions: EmployeeOption[] = (employees || []).map((emp: any) => {
        const firstName = emp.firstName ?? emp.FirstName ?? '';
        const lastName = emp.lastName ?? emp.LastName ?? '';
        const name = `${firstName} ${lastName}`.trim();
        return {
          value: emp.employeeId ?? emp.EmployeeId ?? 0,
          label: name || emp.userName || emp.UserName || `Employee ${emp.employeeId ?? emp.EmployeeId}`,
          departmentId: emp.deptId ?? emp.dept_id ?? emp.departmentId ?? emp.DepartmentId ?? null,
          departmentName: emp.deptName ?? emp.dept_name ?? emp.departmentName ?? emp.DepartmentName ?? '',
        };
      });
      console.log('Employee options with departments:', employeeOptions.slice(0, 5)); // Show first 5
      setAllEmployees(employeeOptions);

      // Load departments
      const deptResult = await ApiRequest({
        urlInfoKey: urlKey.Department,
        type: 'get',
      });

      console.log('Department API response:', deptResult);
      const departments = deptResult?.data?.data ?? deptResult?.data ?? [];
      console.log('Departments data:', departments);

      const departmentOptions: DepartmentOption[] = (departments || []).map((dept: any) => {
        const deptId = dept.deptId ?? dept.dept_id ?? dept.departmentId ?? dept.DepartmentId ?? dept.id ?? dept.Id ?? 0;
        const deptName = dept.deptName ?? dept.dept_name ?? dept.departmentName ?? dept.DepartmentName ?? dept.name ?? dept.Name ?? '';
        return {
          value: deptId,
          label: deptName,
        };
      });
      console.log('Department options:', departmentOptions);
      setAllDepartments(departmentOptions);
    } catch (error: any) {
      message.error(error?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter employees based on search
  useEffect(() => {
    if (!searchText) {
      setFilteredEmployees(allEmployees);
    } else {
      const searchLower = searchText.toLowerCase();
      setFilteredEmployees(
        allEmployees.filter(emp =>
          emp.label.toLowerCase().includes(searchLower)
        )
      );
    }
  }, [searchText, allEmployees]);

  const handleAllToggle = (checked: boolean) => {
    setIsAllRecipients(checked);
    if (checked) {
      setSelectedDepartmentIds([]);
      setSelectedEmployeeIds([]);
    }
    setHasUnsavedChanges(true);
  };

  const handleDepartmentChange = (values: number[]) => {
    console.log('Department selection changed:', values);
    const addedDepts = values.filter(v => !selectedDepartmentIds.includes(v));
    const removedDepts = selectedDepartmentIds.filter(v => !values.includes(v));
    console.log('Added departments:', addedDepts, 'Removed departments:', removedDepts);

    let newEmployeeIds = [...selectedEmployeeIds];

    // Add employees from newly selected departments
    addedDepts.forEach(deptId => {
      const deptEmployees = allEmployees.filter(emp => emp.departmentId === deptId);
      console.log(`Department ${deptId} has ${deptEmployees.length} employees:`, deptEmployees);
      deptEmployees.forEach(emp => {
        if (!newEmployeeIds.includes(emp.value)) {
          newEmployeeIds.push(emp.value);
        }
      });
    });

    // Remove employees from deselected departments
    removedDepts.forEach(deptId => {
      const deptEmployees = allEmployees.filter(emp => emp.departmentId === deptId);
      newEmployeeIds = newEmployeeIds.filter(id =>
        !deptEmployees.some(emp => emp.value === id)
      );
    });

    console.log('New employee IDs after department change:', newEmployeeIds);
    setSelectedDepartmentIds(values);
    setSelectedEmployeeIds(newEmployeeIds);
    setHasUnsavedChanges(true);
  };

  const handleEmployeeToggle = (employeeId: number) => {
    setSelectedEmployeeIds(prev => {
      const newIds = prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId];
      setHasUnsavedChanges(true);
      return newIds;
    });
  };

  const handleConfirm = () => {
    if (!isAllRecipients && selectedEmployeeIds.length === 0) {
      message.warning('Please select at least one employee');
      return;
    }
    const result = isAllRecipients ? [] : selectedEmployeeIds;
    onConfirm(result);
    setHasUnsavedChanges(false);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Close?',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Yes, close',
        cancelText: 'Continue editing',
        onOk: () => {
          setHasUnsavedChanges(false);
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  return (
    <Modal
      title="Select Recipients"
      visible={visible}
      onCancel={handleCancel}
      onOk={handleConfirm}
      okText={`Confirm (${isAllRecipients ? 'All' : selectedEmployeeIds.length})`}
      cancelText="Cancel"
      width={700}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <div style={{ minHeight: 400 }}>
          {/* All recipients toggle */}
          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={isAllRecipients}
              onChange={e => handleAllToggle(e.target.checked)}
            >
              Send to all employees
            </Checkbox>
          </div>

          {/* Department selection */}
          {!isAllRecipients && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Select by Department:</div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select departments"
                value={selectedDepartmentIds}
                onChange={handleDepartmentChange}
                options={allDepartments}
                allowClear
              />
            </div>
          )}

          {/* Employee list */}
          {!isAllRecipients && (
            <div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Select Individual Employees:</div>
              <Input
                placeholder="Search employees..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ marginBottom: 12 }}
                allowClear
              />
              <div
                style={{
                  maxHeight: 250,
                  overflowY: 'auto',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  padding: 8
                }}
              >
                {filteredEmployees.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#999' }}>
                    {searchText ? 'No matching results' : 'No employee data'}
                  </div>
                ) : (
                  filteredEmployees.map(emp => (
                    <div
                      key={emp.value}
                      style={{
                        padding: '6px 8px',
                        cursor: 'pointer',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: selectedEmployeeIds.includes(emp.value)
                          ? '#e6f7ff'
                          : 'transparent'
                      }}
                      onClick={() => handleEmployeeToggle(emp.value)}
                    >
                      <span>{emp.label}</span>
                      <Checkbox checked={selectedEmployeeIds.includes(emp.value)} />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default RecipientSelectorModal;
